/**
 * useRoomInviteUrl.js
 * Hook der beim App-Start prüft ob ein Invite-Link aufgerufen wurde (/r/:slug)
 * und entsprechend handelt:
 * - Eingeloggt: Direkt dem Raum beitreten
 * - Nicht eingeloggt: Slug in localStorage speichern, Login triggern
 */
import { useEffect, useState } from 'react';
import { setPendingRoomJoin } from './usePendingRoomJoin';

/**
 * Extrahiert den Slug aus der URL, falls vorhanden
 * Unterstützt: /r/slug, /r/slug/, ?room=slug
 */
function extractSlugFromUrl() {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // Pattern: /r/my-room-slug
  const roomMatch = pathname.match(/^\/r\/([a-z][a-z0-9-]+)$/i);
  if (roomMatch) {
    return roomMatch[1].toLowerCase();
  }

  // Alternative: ?room=my-room-slug (Fallback)
  const roomParam = searchParams.get('room');
  if (roomParam) {
    return roomParam.toLowerCase();
  }

  return null;
}

/**
 * Bereinigt die URL nach dem Verarbeiten des Invite-Links
 */
function cleanupUrl() {
  const url = new URL(window.location.href);
  const hadRoom = url.pathname.startsWith('/r/') || url.searchParams.has('room');

  if (hadRoom) {
    url.pathname = '/';
    url.searchParams.delete('room');
    window.history.replaceState({}, '', url.pathname + url.search);
    console.log('[RoomInvite] Cleaned up URL');
  }
}

/**
 * Hook zum Verarbeiten von Invite-Links
 *
 * @param {object} options
 * @param {boolean} options.isLoggedIn - Ob User eingeloggt ist
 * @param {string} options.userId - User ID
 * @param {string} options.nickname - Nickname
 * @param {function} options.openLogin - Funktion zum Öffnen des Login-Modals
 * @param {function} options.joinRoom - Funktion zum Beitreten eines Raums
 * @returns {object} - { pendingSlug, isProcessing }
 */
export function useRoomInviteUrl({ isLoggedIn, userId, nickname, openLogin, joinRoom }) {
  const [pendingSlug, setPendingSlug] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const slug = extractSlugFromUrl();

    if (!slug) {
      return; // Kein Invite-Link
    }

    console.log('[RoomInvite] Found invite link for room:', slug);

    if (isLoggedIn && userId && nickname) {
      // User ist eingeloggt → direkt beitreten
      setIsProcessing(true);
      cleanupUrl();

      if (joinRoom) {
        joinRoom(slug)
          .then(() => {
            console.log('[RoomInvite] Successfully joined room:', slug);
          })
          .catch((err) => {
            console.error('[RoomInvite] Failed to join room:', err);
          })
          .finally(() => {
            setIsProcessing(false);
          });
      }
    } else {
      // User nicht eingeloggt → für nach dem Login speichern
      setPendingRoomJoin(slug);
      setPendingSlug(slug);
      cleanupUrl();

      // Login-Modal öffnen
      if (openLogin) {
        console.log('[RoomInvite] Opening login for pending room:', slug);
        openLogin();
      }
    }
  }, [isLoggedIn, userId, nickname, openLogin, joinRoom]);

  return { pendingSlug, isProcessing };
}

export default useRoomInviteUrl;
